import {css} from 'aphrodite';
import React from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ConnectionStatus} from 'instruments/connections/ConnectionConstants';
import {QueryingConnectionSearchInput} from 'instruments/connections/ConnectionSearchInput';
import {
  CONNECTION_STATUS_TO_EQUIPMENT_TEXT,
  CONNECTION_STATUS_TO_ICON,
  CONNECTION_STATUS_TO_STYLE,
  CONNECTION_STATUS_TO_TEXT,
  styles as connectionStatusDisplayStyles,
} from 'instruments/connections/ConnectionStatusDisplay';
import {QueryingEquipmentSearchInput} from 'instruments/equipment/EquipmentSearchInput';
import {getConnectionIdFromEquipment} from 'instruments/equipment/getConnectionIdFromEquipment';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {FilterKey, keyToEmptyFilter} from 'search/SearchConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ConnectionChip from 'ui-library/chips/ConnectionChip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AssayRunField} from './AssayRunFields';

interface AssayDataSourceProps {
  shouldRecordEquipment: boolean;
  selectedEquipmentId: string | null;
  adapterId: string | null;
  connectionId: string | null;
  // Is the Notebook in a print-preview or export mode?
  viewer?: boolean;
}

interface AssayDataSourceSelectorProps extends AssayDataSourceProps {
  onUpdateEquipment: (cid: string, equipmentId: string | null, connectionId?: string | null) => void;
  onUpdateConnection: (cid: string, connectionId: string | null) => void;
  cid: string;
  hasAutomationFiles: boolean;
}

interface ReadOnlyAssayDataSourceProps extends AssayDataSourceProps {
  connectionStatus?: ConnectionStatus | null;
}

export default function AssayDataSourceSelector(props: AssayDataSourceSelectorProps): JSX.Element | null {
  const {adapterId, connectionId, selectedEquipmentId, onUpdateEquipment} = props;

  const isConnectionEditable =
    isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}) && !props.hasAutomationFiles;
  const isEquipmentEditable = !props.hasAutomationFiles;
  const hasAdapter = Boolean(adapterId);
  if (props.shouldRecordEquipment && !hasAdapter) {
    return (
      <AssayRunField title="Equipment" key="equipment">
        <QueryingEquipmentSearchInput
          equipmentId={selectedEquipmentId}
          onChange={(equipment) => {
            const equipmentId = equipment[0]?.id ? equipment[0]?.id : null;
            onUpdateEquipment(props.cid, equipmentId);
          }}
          shouldAllowMultiple={false}
        />
      </AssayRunField>
    );
  } else if (props.shouldRecordEquipment && hasAdapter) {
    return (
      <AssayRunField title="Equipment" key="equipment">
        <QueryingEquipmentSearchInput
          equipmentId={selectedEquipmentId}
          onChange={async (equipment) => {
            const equipmentId = equipment[0]?.id ? equipment[0]?.id : null;
            const connectionId = await getConnectionIdFromEquipment(equipmentId, adapterId);
            onUpdateEquipment(props.cid, equipmentId, connectionId);
          }}
          shouldAllowMultiple={false}
          adapterId={adapterId}
          isReadOnly={!isEquipmentEditable}
        />
      </AssayRunField>
    );
  } else if (!props.shouldRecordEquipment && hasAdapter) {
    return (
      <AssayRunField title="Connection" key="connection">
        <QueryingConnectionSearchInput
          autoFocus={false}
          onChange={(connections) => {
            props.onUpdateConnection(props.cid, connections[0]?.id);
          }}
          connectionIds={connectionId ? [connectionId] : []}
          shouldAllowMultiple={false}
          isReadOnly={!isConnectionEditable}
          extraFilters={
            adapterId
              ? [
                  {
                    ...keyToEmptyFilter(FilterKey.CONNECTION_ADAPTER),
                    value: [adapterId],
                  },
                ]
              : []
          }
        />
      </AssayRunField>
    );
  } else {
    return null;
  }
}

export function AssayDataSource(props: ReadOnlyAssayDataSourceProps): JSX.Element | null {
  const {adapterId, connectionId} = props;
  const hasAdapter = Boolean(adapterId);
  if (props.shouldRecordEquipment) {
    return (
      <AssayRunField title="Equipment" key="equipment">
        {props.selectedEquipmentId && <BioentityChip id={props.selectedEquipmentId} size={ChipSize.SMALL} />}
        {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS &&
          connectionId &&
          props.connectionStatus &&
          props.connectionStatus !== ConnectionStatus.AVAILABLE && (
            <Tooltip
              container="body"
              placement={TooltipPlacement.TOP}
              title={CONNECTION_STATUS_TO_EQUIPMENT_TEXT[props.connectionStatus]}
            >
              <BenchlingIcon
                className={css(
                  connectionStatusDisplayStyles.statusIcon,
                  connectionStatusDisplayStyles[CONNECTION_STATUS_TO_STYLE[props.connectionStatus]]
                )}
                icon={CONNECTION_STATUS_TO_ICON[props.connectionStatus]}
              />
            </Tooltip>
          )}
      </AssayRunField>
    );
  } else if (hasAdapter) {
    return (
      <AssayRunField title="Connection" key="connection">
        {connectionId && <ConnectionChip id={connectionId} size={ChipSize.SMALL} />}
        {/* only render the status icon for non-available statuses */}
        {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS &&
          !props.viewer &&
          connectionId &&
          !props.connectionStatus && <div {...loadingIndicatorAttrs()} />}
        {!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS &&
          !props.viewer &&
          connectionId &&
          props.connectionStatus &&
          props.connectionStatus !== ConnectionStatus.AVAILABLE && (
            <Tooltip
              container="body"
              placement={TooltipPlacement.TOP}
              title={CONNECTION_STATUS_TO_TEXT[props.connectionStatus]}
            >
              <BenchlingIcon
                className={css(
                  connectionStatusDisplayStyles.statusIcon,
                  connectionStatusDisplayStyles[CONNECTION_STATUS_TO_STYLE[props.connectionStatus]]
                )}
                icon={CONNECTION_STATUS_TO_ICON[props.connectionStatus]}
              />
            </Tooltip>
          )}
      </AssayRunField>
    );
  } else {
    return null;
  }
}
